import { useState } from "react";
import { createPortal } from "react-dom";

export interface JtlLoeschDialogProps {
  orderNo: string;
  onAbbrechen: () => void;
  onBestaetigen: (grund: string) => void;
}

/**
 * Zweistufige Bestaetigung fuer eine unumkehrbare Aktion (Loeschen einer EK-Bestellung IN JTL,
 * nicht nur im Hub-Overlay) - im Unterschied zum bestehenden, einfachen "Loeschen"-Knopf im Drawer
 * (Drawer.tsx, entfernt nur die Hub-Zeile). Muster nach UeberschreibDialog.tsx (DocsTab): Portal auf
 * document.body, gleiche board-editor-*-Klassen (inkl. Kopf-/Koerper-Wrapper), Bestaetigen-Button
 * erst aktiv wenn beide Felder gueltig sind.
 *
 * ================= Zwei Befunde aus dem Review (siehe Task-Bericht) =================
 *
 * 1. CSS-Gate: `.board-editor-overlay` sperrt sich selbst per `opacity: 0; pointer-events: none`
 *    (`bestand.css`) und wird erst durch die ZUSAETZLICHE Klasse `.open` sicht- und bedienbar - jeder
 *    andere Verbraucher dieser Klasse traegt `open` mit (`PasswortDialog.tsx`, `SpaltenEditor.tsx`,
 *    `ListenEditor.tsx`, `VorlagenDialog.tsx`, `PositionenPopup.tsx`, `Artikelbild.tsx`, `App.tsx`).
 *    Hier deshalb fest gesetzt (kein steuerbarer Zustand noetig - die Komponente existiert nur,
 *    solange sie eingehaengt ist).
 * 2. Leere Bestellnummer: eine gerade erst in JTL angelegte Bestellung ohne vergebene Nummer hat
 *    `orderNo === ""` (`api/jtlBestellungen.ts`, `?? ""`). Ohne Schutz wuerde JEDER eingetippte Text
 *    `eingabe.trim() === orderNo` erfuellen und die zweistufige Sicherung waere genau in dem Fall
 *    wirkungslos, in dem sie am wichtigsten ist ("aus Versehen angelegt, sofort wieder weg"). Deshalb
 *    zusaetzlich `orderNoGetrimmt.length > 0`.
 *
 * Ausserdem (Important, gleiche Runde):
 *
 * 3. Kopf-/Koerper-Wrapper (`board-editor-head`/`board-editor-head-copy`/`board-editor-body`)
 *    nachgezogen - ohne sie tragen Ueberschrift, Text und Felder kein Padding (das haengt in
 *    `bestand.css` an genau diesen Klassen, nicht am Dialograhmen selbst). Die Felder selbst
 *    stehen jetzt in `styles/neubau/jtlLoeschDialog.css` (Breite, Feldgestaltung) - NICHT in
 *    `bestand.css`, das zeichengleich zum `<style>`-Block aus `index.html` bleiben muss
 *    (`test/reference/stylesheet.test.ts`) und fuer diesen Dialog keine Entsprechung dort hat.
 *    Jedes Feld ist wie bei `PasswortDialog.tsx` ein `<label>Text<input/></label>` (implizite
 *    Zuordnung statt `htmlFor`/`id`) - `getByLabelText` findet beide Formen gleichermassen.
 * 4. `.btn-danger` traegt in `bestand.css` `margin-left: auto` - richtig fuer seinen eigentlichen
 *    Ort (`.drawer-foot`, wo es den Knopf vom Rest abtrennt), aber falsch hier: in
 *    `.board-editor-foot` (bereits `justify-content: flex-end`) reisst das den "Abbrechen"-Knopf an
 *    den linken Rand statt dass beide Knoepfe zusammen rechts stehen. `.jtl-loesch-dialog
 *    .btn-danger` (siehe `styles/neubau/jtlLoeschDialog.css`) setzt `margin-left` deshalb lokal
 *    zurueck - die rote Einfaerbung (der eigentliche "Danger"-Charakter) bleibt erhalten, nur die
 *    Fussleisten-Anordnung nicht mehr zerstoert.
 */
export function JtlLoeschDialog({ orderNo, onAbbrechen, onBestaetigen }: JtlLoeschDialogProps) {
  const [eingabe, setEingabe] = useState("");
  const [grund, setGrund] = useState("");
  const orderNoGetrimmt = orderNo.trim();
  const bereit =
    orderNoGetrimmt.length > 0 && eingabe.trim() === orderNoGetrimmt && grund.trim().length > 0;

  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onAbbrechen(); }}
    >
      <section
        className="board-editor-dialog jtl-loesch-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jtlLoeschTitel"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="jtlLoeschTitel">EK-Bestellung {orderNo} in JTL löschen</h2>
            <p>
              Diese Aktion löscht die Bestellung endgültig in JTL, nicht nur im Hub. Sie kann nicht
              rückgängig gemacht werden.
            </p>
          </div>
        </div>
        <div className="board-editor-body">
          <label>
            Bestellnummer zur Bestätigung ({orderNo})
            <input
              value={eingabe}
              onChange={(e) => setEingabe(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label>
            Grund (Pflichtfeld)
            <textarea
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
            />
          </label>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>Abbrechen</button>
          <button
            className="btn-danger"
            type="button"
            disabled={!bereit}
            onClick={() => onBestaetigen(grund.trim())}
          >
            Endgültig löschen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
